import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/admin/searches")({
  head: () => ({ meta: [{ title: "Admin — Site Searches" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: SearchesPage,
});

type Row = {
  id: string;
  query: string;
  normalized_query: string;
  results_count: number | null;
  clicked_result: string | null;
  page_path: string | null;
  referrer: string | null;
  created_at: string;
};

type Top = { normalized_query: string; count: number; zero: number };

function SearchesPage() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [top, setTop] = useState<Top[]>([]);
  const [limit, setLimit] = useState(100);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate({ to: "/auth" }); return; }
      const { data: roles } = await supabase
        .from("user_roles").select("role").eq("user_id", user.id);
      const admin = !!roles?.some((r) => r.role === "admin");
      setIsAdmin(admin);
      setAuthChecked(true);
      if (!admin) return;
    })();
  }, [navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    setLoading(true);
    (async () => {
      const { data, error: e } = await supabase
        .from("search_queries")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(limit);
      if (e) setError(e.message);
      else {
        setRows((data ?? []) as Row[]);
        const counts = new Map<string, { count: number; zero: number }>();
        (data ?? []).forEach((r: Row) => {
          const key = r.normalized_query;
          const cur = counts.get(key) ?? { count: 0, zero: 0 };
          cur.count += 1;
          if ((r.results_count ?? 0) === 0) cur.zero += 1;
          counts.set(key, cur);
        });
        const t = Array.from(counts.entries())
          .map(([normalized_query, v]) => ({ normalized_query, ...v }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 20);
        setTop(t);
      }
      setLoading(false);
    })();
  }, [isAdmin, limit]);

  if (!authChecked) return <div className="mx-auto max-w-5xl p-8">Checking access…</div>;
  if (!isAdmin) return <div className="mx-auto max-w-5xl p-8">Admin access required.</div>;

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold text-navy-deep">Site Searches</h1>
        <Link to="/admin" className="text-sm text-navy-deep underline">← Admin</Link>
      </div>

      <section className="border border-navy-deep/15 bg-white p-4">
        <h2 className="mb-3 font-display text-lg font-bold text-navy-deep">Top searches (last {rows.length})</h2>
        {top.length === 0 ? (
          <p className="text-sm text-navy-deep/70">No searches yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-navy-deep/60">
              <tr><th className="py-2">Query</th><th className="py-2">Count</th><th className="py-2">Zero-result</th></tr>
            </thead>
            <tbody>
              {top.map((t) => (
                <tr key={t.normalized_query} className="border-t border-navy-deep/10">
                  <td className="py-2 font-mono">{t.normalized_query}</td>
                  <td className="py-2">{t.count}</td>
                  <td className="py-2">{t.zero > 0 ? <span className="text-red">{t.zero}</span> : t.zero}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="border border-navy-deep/15 bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-navy-deep">Recent searches</h2>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="border border-navy-deep/20 bg-white px-2 py-1 text-sm"
          >
            {[50, 100, 250, 500].map((n) => <option key={n} value={n}>Last {n}</option>)}
          </select>
        </div>
        {error && <p className="text-sm text-red">{error}</p>}
        {loading ? (
          <p className="text-sm">Loading…</p>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-navy-deep/60">
                <tr>
                  <th className="py-2">When</th>
                  <th className="py-2">Query</th>
                  <th className="py-2">Results</th>
                  <th className="py-2">Clicked</th>
                  <th className="py-2">From page</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-navy-deep/10">
                    <td className="py-2 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="py-2">{r.query}</td>
                    <td className="py-2">{r.results_count ?? "—"}</td>
                    <td className="py-2 text-xs">{r.clicked_result ?? <span className="text-navy-deep/70">—</span>}</td>
                    <td className="py-2 text-xs">{r.page_path ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
